import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useI18n } from "@/i18n/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Journal() {
  const { t, lang } = useI18n();
  const j = t.journal;
  const [articles, setArticles] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/articles`).then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  const visible = articles.filter((a) => a.lang === lang);
  const list = visible.length > 0 ? visible : articles;
  if (list.length === 0) return null;

  return (
    <section id="journal" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <div className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-copper">
          {j.eyebrow}
        </div>
        <h2 className="font-serif text-3xl leading-none tracking-tight text-pine sm:text-5xl">
          {j.titleA} <span className="italic text-copper">{j.titleEm}</span>
        </h2>
        <p className="mt-6 text-base font-light leading-relaxed text-sand sm:text-lg">
          {j.sub}
        </p>
      </motion.div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="journal-grid">
        {list.map((art, i) => {
          const open = openId === art.id;
          return (
            <motion.article
              key={art.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
              data-testid={`journal-article-${art.id}`}
              className={`border border-pine/10 bg-paper p-6 shadow-sm transition-shadow duration-500 hover:shadow-lg hover:shadow-pine/5 sm:p-8 ${open ? "md:col-span-2 lg:col-span-3" : ""}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
                {new Date(art.created_at).toLocaleDateString()} · {art.lang.toUpperCase()}
              </div>
              <h3 className="mt-3 font-serif text-2xl tracking-tight text-pine">{art.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-sand">{art.excerpt}</p>

              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 whitespace-pre-line border-t border-pine/10 pt-4 text-sm font-light leading-relaxed text-bark">
                      {art.content}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="button"
                data-testid={`journal-toggle-${art.id}`}
                onClick={() => setOpenId(open ? null : art.id)}
                className="copper-underline mt-5 text-xs font-medium uppercase tracking-[0.2em] text-copper"
              >
                {open ? j.close : j.read}
              </button>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}
